import React from 'react';

/**
 * Editorial design system primitives — slate-gradient hero,
 * mono caps labels, serif italic accent. Mirrors the iOS
 * "Slate forms" language for the athlete-facing web pages.
 *
 * Names match the iOS primitives in `RecruitingFormPrimitives.swift`
 * so cross-platform fixes stay obvious.
 */

/* MonoLabel — uppercase tracked caption, e.g. "DASHBOARD · ATHLETE" */
export function MonoLabel({ children, onDark = false, accent = false, className = '', ...rest }) {
  const cls = ['ed-mono'];
  if (onDark) cls.push('ed-mono--on-dark');
  if (accent) cls.push('ed-mono--accent');
  if (className) cls.push(className);
  return <span className={cls.join(' ')} {...rest}>{children}</span>;
}

/* SectionCap — section header with mono label + optional action */
export function SectionCap({ label, hint, action, onAction }) {
  return (
    <div className="ed-cap">
      <span className="ed-cap__label">{label}</span>
      {hint && <span className="ed-cap__hint">{hint}</span>}
      {action && (
        <button type="button" className="ed-cap__action" onClick={onAction}>
          {action}
        </button>
      )}
    </div>
  );
}

/* StatStrip — horizontal row of mono-caps stats inside the hero band.
   Items: [{ label, value }] — render '—' for missing values. */
export function StatStrip({ items }) {
  return (
    <div className="ed-hero__strip" role="list">
      {items.map((it, i) => {
        const isEmpty = it.value === null || it.value === undefined || it.value === '';
        return (
          <div key={i} className="ed-stat" role="listitem">
            <span className={`ed-stat__value${isEmpty ? ' ed-stat__value--empty' : ''}`}>
              {isEmpty ? '—' : it.value}
            </span>
            <span className="ed-stat__label">{it.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * EditorHero — slate gradient panel with mono eyebrow, serif headline
 * (last word can be italic accent), optional subtitle, action buttons,
 * and optional bottom stat strip.
 *
 * Props:
 *   eyebrow:        ReactNode — small mono-caps line(s) at top
 *   headline:       string — main headline (sans serif)
 *   accent:         string — final phrase rendered serif italic
 *   sub:            string — secondary line under headline
 *   actions:        ReactNode — button row
 *   stats:          [{ label, value }] — bottom strip
 *   prefix:         ReactNode — left-side element (e.g. monogram tile)
 */
export function EditorHero({ eyebrow, headline, accent, sub, actions, stats, prefix }) {
  return (
    <section className="ed-hero">
      <div className="ed-hero__body">
        {eyebrow && <div className="ed-hero__eyebrow">{eyebrow}</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          {prefix}
          <h1 className="ed-hero__headline">
            {headline}
            {accent && (
              <>
                {' '}
                <span className="ed-hero__headline-accent">{accent}</span>
              </>
            )}
          </h1>
        </div>

        {sub && <p className="ed-hero__sub">{sub}</p>}
        {actions && <div className="ed-hero__actions">{actions}</div>}
      </div>

      {stats && stats.length > 0 && <StatStrip items={stats} />}
    </section>
  );
}

/* Monogram — orange initials tile (used in Profile hero) */
export function Monogram({ initials, photoUrl }) {
  if (photoUrl) {
    return (
      <div className="ed-monogram ed-monogram--photo">
        <img src={photoUrl} alt="" />
      </div>
    );
  }
  return <div className="ed-monogram">{initials}</div>;
}

/* EditorialButton — flat mono-caps button. Variants: solid | ghost | light */
export function EditorialButton({ variant = 'solid', children, onClick, type = 'button', ...rest }) {
  return (
    <button
      type={type}
      className={`ed-btn ed-btn--${variant}`}
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * PageHeader — smaller, light-bg page header for non-dashboard pages.
 * Mono eyebrow + serif headline (with optional italic accent) + sub + actions.
 */
export function PageHeader({ eyebrow, title, accent, sub, actions }) {
  return (
    <header className="ed-pageheader">
      {eyebrow && <div className="ed-pageheader__eyebrow">{eyebrow}</div>}
      <h1 className="ed-pageheader__title">
        {title}
        {accent && (
          <>
            {' '}
            <span className="ed-pageheader__title-accent">{accent}</span>
          </>
        )}
      </h1>
      {sub && <p className="ed-pageheader__sub">{sub}</p>}
      {actions && <div className="ed-pageheader__actions">{actions}</div>}
    </header>
  );
}

/* Field — form field wrapper (mono caps label + slot for input) */
export function Field({ label, hint, children, className = '' }) {
  return (
    <div className={`ed-field ${className}`}>
      {label && <label className="ed-field__label">{label}</label>}
      {children}
      {hint && <span className="ed-field__hint">{hint}</span>}
    </div>
  );
}

/* Pill — chip / tag. Variant: active | ghost | green | orange | purple | yellow | red */
export function Pill({ variant = '', as = 'span', onClick, children, ...rest }) {
  const cls = ['ed-pill'];
  if (variant) cls.push(`ed-pill--${variant}`);
  const Tag = as;
  return (
    <Tag className={cls.join(' ')} onClick={onClick} {...rest}>{children}</Tag>
  );
}

/* EmptyState */
export function EmptyState({ icon, title, body, action }) {
  return (
    <div className="ed-empty">
      {icon && <div className="ed-empty__icon">{icon}</div>}
      {title && <h3 className="ed-empty__title">{title}</h3>}
      {body && <p className="ed-empty__body">{body}</p>}
      {action}
    </div>
  );
}

/* DateTile — camp date tile (month + day on color background).
   Variant: orange | blue | green | purple | brown | red | ink */
export function DateTile({ month, day, variant = 'orange' }) {
  return (
    <div className={`ed-datetile ed-datetile--${variant}`}>
      <span className="ed-datetile__month">{month}</span>
      <span className="ed-datetile__day">{day}</span>
    </div>
  );
}

/* TaskCard — clickable task tile (Get Verified, Find Partner, etc.) */
export function TaskCard({ cap, title, body, icon, iconVariant = '', onClick }) {
  const iconCls = ['ed-task__icon'];
  if (iconVariant) iconCls.push(`ed-task__icon--${iconVariant}`);
  return (
    <button type="button" className="ed-task" onClick={onClick}>
      {icon && <span className={iconCls.join(' ')}>{icon}</span>}
      <span style={{ flex: 1, minWidth: 0 }}>
        {cap && <div className="ed-task__cap">{cap}</div>}
        <h3 className="ed-task__title">{title}</h3>
        {body && <p className="ed-task__body">{body}</p>}
      </span>
    </button>
  );
}
